import type * as MusicKitGlobalNamespace from '@amp/musickit';
import type { MusicKitInstance as MusicKit } from '@amp/musickit/-internals/musickit-instance';
import { APP, PAF_CONFIG } from '~/config/musickit';
import { JWT } from '~/config/media-api';

export type { MediaItem, Me } from '@amp/musickit';
export type { MusicKitInstance as MusicKit } from '@amp/musickit/-internals/musickit-instance';
export { PlaybackStates } from '@amp/web-apps-common/src/playback/queue-store';

export type MusicKitGlobal = typeof MusicKitGlobalNamespace;
export type MKError = MusicKitGlobalNamespace.MKError;

interface WindowWithMusicKit extends Window {
    MusicKit?: MusicKitGlobal;
}

declare const window: WindowWithMusicKit;

/**
 * Retrieves the window.MusicKit global loaded by a <script> tag already
 * present in the markup when it becomes available.
 *
 * @returns MusicKit a promise resolving to the window.MusicKit global
 */
async function getMusicKitGlobal(): Promise<MusicKitGlobal> {
    if (window.MusicKit) {
        return window.MusicKit;
    }

    return new Promise((resolve, reject) => {
        function onMusicKitLoaded() {
            document.removeEventListener('musickitloaded', onMusicKitLoaded);

            // The event should signal that it exists
            const musicKit = window.MusicKit;

            if (musicKit) {
                resolve(musicKit);
            } else {
                reject(
                    new Error(
                        'window.MusicKit not defined after musickitloaded',
                    ),
                );
            }
        }

        document.addEventListener('musickitloaded', onMusicKitLoaded);
    });
}

/**
 * Configures an instance of MusicKit loaded from a <script> on the page for
 * music playback in a given storefront.
 *
 * @param storefront The two letter storefront country code
 * @return musicKit The instance of MusicKit
 */
export async function configureMusicKit(storefront: string): Promise<{
    musicKit: MusicKit;
    musicKitGlobal: typeof MusicKitGlobalNamespace;
}> {
    const musicKitGlobal = await getMusicKitGlobal();

    let musicKit;
    try {
        musicKit = await musicKitGlobal.configure({
            developerToken: JWT,
            app: APP,
            bitrate: musicKitGlobal.PlaybackBitrate.HIGH,
            disableLogoutURL: true,
            meParameters: {
                'challenge[subscriptionCapabilities]': ['voice,premium'],
            },
            persist: 'cookie',
            playbackActions: [musicKitGlobal.PlaybackActions.REPEAT],
            storefrontId: storefront,
            suppressErrorDialog: true,
            ...PAF_CONFIG,
        });
    } catch (e) {
        throw new Error('error while loading MusicKit');
    }

    return {
        musicKitGlobal,
        musicKit,
    };
}
